// src/components/UI.jsx
import { useCallback, useRef, useState } from "react";

// ─── Design tokens ────────────────────────────────────────────────────────────
export const B = {
  orange:       "#E06B4F",
  orangeLight:  "#F1977B",
  orangeDark:   "#BE4E35",
  orangePale:   "#FFF7F3",
  orangeFaint:  "#FFF2EC",
  orangeMid:    "#F7D7CA",
  cream:        "#FFFCF9",
  headerBg:     "#FFFFFF",
  text:         "#2E2926",
  textMid:      "#7A6256",
  textLight:    "#B69381",
  border:       "#EFDACF",
  borderLight:  "#F7EDE6",
  white:        "#FFFFFF",
  red:          "#C0392B",
  redLight:     "#FADBD8",
  green:        "#1E8449",
  greenLight:   "#D5F5E3",
  gold:         "#C9971C",
  goldLight:    "#F5E6B8",
};

export const fonts = {
  display: "'Playfair Display', Georgia, serif",
  adminDisplay: "'DM Sans', system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
  body:    "'DM Sans', system-ui, sans-serif",
};

// ─── Nilam Logo ───────────────────────────────────────────────────────────────
export const NilamLogo = ({ size = 44, logoUrl }) =>
  logoUrl ? (
    <img src={logoUrl} alt="Nilam Synthetics"
      style={{ width: size, height: size, objectFit: "contain" }} />
  ) : (
    <svg width={size} height={size} viewBox="0 0 110 110" fill="none">
      <circle cx="55" cy="55" r="28" fill={B.orange} />
      <circle cx="55" cy="55" r="36" stroke={B.orange}  strokeWidth="1.4" fill="none" opacity="0.8" />
      <circle cx="55" cy="55" r="42" stroke="#4CAF50"   strokeWidth="1"   fill="none" opacity="0.6" />
      <circle cx="55" cy="55" r="46" stroke="#2196F3"   strokeWidth="0.8" fill="none" opacity="0.5" />
      <circle cx="55" cy="55" r="49" stroke="#E53935"   strokeWidth="0.7" fill="none" opacity="0.45" />
      <line x1="42" y1="80" x2="76" y2="20" stroke={B.orange} strokeWidth="2.2" strokeLinecap="round" />
      <circle cx="76" cy="18" r="4" stroke={B.orange} strokeWidth="2.2" fill="none" />
      <text x="38" y="70" fontFamily="Georgia,serif" fontSize="38" fontStyle="italic" fontWeight="bold" fill="white">n</text>
    </svg>
  );

// ─── Badge ────────────────────────────────────────────────────────────────────
export const Badge = ({ children, color = B.orange, size = "sm" }) => (
  <span style={{
    background: color + "18", color, border: `1px solid ${color}44`,
    borderRadius: 4, padding: size === "sm" ? "2px 8px" : "4px 12px",
    fontSize: size === "sm" ? 10 : 12, fontWeight: 700,
    letterSpacing: "0.06em", whiteSpace: "nowrap", textTransform: "uppercase",
    fontFamily: fonts.body,
  }}>{children}</span>
);

// Product badge with preset styles
export const ProductBadge = ({ type }) => {
  const styles = {
    new:        { label: "New Arrival", color: "#1E8449" },
    bestseller: { label: "Bestseller",  color: "#C9971C" },
    sale:       { label: "Sale",        color: "#C0392B" },
    featured:   { label: "Featured",   color: B.orange   },
  };
  const s = styles[type];
  if (!s) return null;
  return (
    <span style={{
      background: s.color, color: "#fff",
      padding: "3px 10px", fontSize: 9, fontWeight: 700,
      letterSpacing: "0.1em", textTransform: "uppercase",
      fontFamily: fonts.body, borderRadius: 3,
    }}>{s.label}</span>
  );
};

// ─── Input ────────────────────────────────────────────────────────────────────
export const Input = ({ label, textarea, select, children, ...props }) => {
  const shared = {
    width: "100%", background: B.white, border: `1.5px solid ${B.border}`,
    borderRadius: 8, padding: "10px 13px", color: B.text, fontSize: 13,
    outline: "none", boxSizing: "border-box", transition: "border 0.2s",
    fontFamily: fonts.body, resize: textarea ? "vertical" : undefined,
    appearance: select ? "auto" : undefined, ...props.style,
  };
  return (
    <div style={{ marginBottom: 14 }}>
      {label && (
        <label style={{ display: "block", fontSize: 10, fontWeight: 600,
          color: B.textMid, marginBottom: 5, letterSpacing: "0.1em",
          textTransform: "uppercase", fontFamily: fonts.body }}>
          {label}
        </label>
      )}
      {textarea
        ? <textarea {...props} style={shared}
            onFocus={e => e.target.style.borderColor = B.orange}
            onBlur={e  => e.target.style.borderColor = B.border} />
        : select
        ? <select {...props} style={shared}>{children}</select>
        : <input  {...props} style={shared}
            onFocus={e => e.target.style.borderColor = B.orange}
            onBlur={e  => e.target.style.borderColor = B.border} />
      }
    </div>
  );
};

// ─── Button ───────────────────────────────────────────────────────────────────
export const Btn = ({ children, onClick, variant = "primary", small, fullWidth, style = {}, disabled, type = "button" }) => {
  const [hov, setHov] = useState(false);
  const base = {
    border: "none", borderRadius: 8, cursor: disabled ? "not-allowed" : "pointer",
    fontWeight: 600, letterSpacing: "0.04em", transition: "all 0.2s",
    fontSize: small ? 11 : 13, padding: small ? "6px 14px" : "11px 24px",
    fontFamily: fonts.body, width: fullWidth ? "100%" : undefined,
    opacity: disabled ? 0.5 : 1, ...style,
  };
  const v = {
    primary:   { background: hov ? B.orangeDark : B.orange, color: B.white, boxShadow: hov ? `0 6px 20px ${B.orange}44` : "none" },
    secondary: { background: hov ? B.orangeMid  : B.orangeFaint, color: B.orangeDark, border: `1.5px solid ${B.orangeMid}` },
    danger:    { background: hov ? "#FADBD8" : B.redLight, color: B.red, border: `1px solid ${B.red}33` },
    ghost:     { background: hov ? B.orangeFaint : "transparent", color: B.orange, border: `1.5px solid ${B.border}` },
    ghostDark: { background: hov ? "rgba(255,255,255,0.1)" : "transparent", color: "rgb(122, 98, 86)", border: "1.5px solid rgba(255,255,255,0.2)" },
    success:   { background: hov ? "#1A7A40" : B.green, color: B.white },
    gold:      { background: hov ? "#A07A10" : B.gold,  color: B.white },
  };
  return (
    <button type={type} onClick={!disabled ? onClick : undefined}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ ...base, ...(v[variant] || v.primary) }}>
      {children}
    </button>
  );
};


// ─── Common Confirmation Modal ───────────────────────────────────────────────
export const ConfirmModal = ({
  open,
  title = "Confirm action",
  message = "Are you sure you want to continue?",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
}) => {
  if (!open) return null;

  return (
    <div
      role="presentation"
      onMouseDown={onCancel}
      style={{
        position: "fixed", inset: 0, zIndex: 10000,
        background: "rgba(46, 41, 38, 0.42)",
        backdropFilter: "blur(3px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, animation: "fadeIn 0.18s ease",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        aria-describedby="confirm-modal-message"
        onMouseDown={e => e.stopPropagation()}
        style={{
          width: "min(100%, 420px)", background: B.white,
          border: `1px solid ${B.borderLight}`, borderRadius: 18,
          boxShadow: "0 24px 70px rgba(46, 41, 38, 0.24)",
          padding: 24, fontFamily: fonts.body, color: B.text,
          animation: "modalPop 0.2s ease",
        }}
      >
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          <div style={{
            width: 42, height: 42, borderRadius: "50%",
            background: B.redLight, color: B.red,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20, flexShrink: 0,
          }}>
            !
          </div>
          <div style={{ minWidth: 0 }}>
            <h3 id="confirm-modal-title" style={{
              margin: "0 0 7px", fontSize: 18, fontWeight: 800,
              color: B.text, fontFamily: fonts.adminDisplay,
            }}>
              {title}
            </h3>
            <p id="confirm-modal-message" style={{
              margin: 0, fontSize: 13, lineHeight: 1.65, color: B.textMid,
              fontFamily: fonts.body,
            }}>
              {message}
            </p>
          </div>
        </div>

        <div style={{
          display: "flex", justifyContent: "flex-end", gap: 10,
          marginTop: 24, flexWrap: "wrap",
        }}>
          <Btn onClick={onCancel} variant="ghost">{cancelLabel}</Btn>
          <Btn onClick={onConfirm} variant="danger">{confirmLabel}</Btn>
        </div>
      </div>
    </div>
  );
};

export const useConfirmModal = () => {
  const [modal, setModal] = useState(null);
  const resolverRef = useRef(null);

  const close = useCallback((result) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setModal(null);
  }, []);

  const confirm = useCallback((options = {}) => new Promise(resolve => {
    resolverRef.current = resolve;
    setModal(options);
  }), []);

  const confirmModal = (
    <ConfirmModal
      open={Boolean(modal)}
      {...(modal || {})}
      onCancel={() => close(false)}
      onConfirm={() => close(true)}
    />
  );

  return { confirm, confirmModal };
};

// ─── Image Upload ─────────────────────────────────────────────────────────────
export const ImageUpload = ({ preview, onFile, label, hint, width = 88, height = 108, multiple }) => {
  const id = `img-${Math.random().toString(36).slice(2)}`;
  const handleChange = e => {
    if (multiple) { onFile(Array.from(e.target.files)); }
    else { const f = e.target.files[0]; if (f) onFile(f); }
  };
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ display: "block", fontSize: 10, fontWeight: 600, color: B.textMid, marginBottom: 8, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: fonts.body }}>{label}</label>}
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <label htmlFor={id} style={{ width, height, background: B.orangeFaint, border: `2px dashed ${B.orange}66`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", overflow: "hidden", flexShrink: 0, transition: "border-color 0.2s" }}>
          {preview
            ? <img src={typeof preview === "string" ? preview : URL.createObjectURL(preview)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <div style={{ textAlign: "center", color: B.textLight }}>
                <div style={{ fontSize: multiple ? 20 : 24 }}>{multiple ? "🖼️" : "📷"}</div>
                <div style={{ fontSize: 9, marginTop: 4, fontFamily: fonts.body }}>{multiple ? "Upload Photos" : "Upload"}</div>
              </div>}
        </label>
        {hint && <p style={{ fontSize: 12, color: B.textMid, lineHeight: 1.7, paddingTop: 6, margin: 0, fontFamily: fonts.body }}>{hint}</p>}
      </div>
      <input id={id} type="file" accept="image/*" multiple={multiple} onChange={handleChange} style={{ display: "none" }} />
    </div>
  );
};

// ─── Card ─────────────────────────────────────────────────────────────────────
export const Card = ({ children, style = {} }) => (
  <div style={{ background: B.white, border: `1px solid ${B.borderLight}`, borderRadius: 14, padding: 24, ...style }}>
    {children}
  </div>
);

// ─── Section Title ────────────────────────────────────────────────────────────
export const SectionTitle = ({ children, sub, center }) => (
  <div style={{ marginBottom: 24, textAlign: center ? "center" : "left" }}>
    <h2 style={{ margin: 0, fontWeight: 700, fontSize: 22, color: B.text, fontFamily: fonts.adminDisplay }}>{children}</h2>
    {sub && <p style={{ margin: "5px 0 0", fontSize: 13, color: B.textLight, fontFamily: fonts.body }}>{sub}</p>}
  </div>
);

// ─── Toast ────────────────────────────────────────────────────────────────────
export const Toast = ({ toast }) =>
  toast ? (
    <div style={{ position: "fixed", bottom: 28, right: 28, zIndex: 9999, background: toast.color || B.orange, color: B.white, padding: "13px 24px", borderRadius: 10, fontWeight: 600, fontSize: 13, boxShadow: "0 8px 32px rgba(0,0,0,0.22)", animation: "fadeUp 0.3s", fontFamily: fonts.body }}>
      {toast.msg}
    </div>
  ) : null;

// ─── Spinner ──────────────────────────────────────────────────────────────────
export const Spinner = ({ size = 90, full }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: full ? "120px 0" : "40px 0" }}>
    <video
      src="/brand-intro.mp4"
      autoPlay loop muted playsInline
      aria-label="Loading"
      style={{ width: size, height: size, objectFit: "contain" }}
    />
  </div>
);

// ─── Divider ──────────────────────────────────────────────────────────────────
export const Divider = ({ label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 14, margin: "24px 0" }}>
    <div style={{ flex: 1, height: 1, background: B.borderLight }} />
    {label && <span style={{ fontSize: 11, color: B.textLight, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: fonts.body }}>{label}</span>}
    <div style={{ flex: 1, height: 1, background: B.borderLight }} />
  </div>
);
